/**
 * voice.ts — what a dictated sentence means: plain text to type, or a spoken command.
 *
 * A command counts only when the command word is the very first word. Anywhere else the words are text, so a
 * sentence such as "Please save this for later" can never trigger anything. Publishing, approving, rejecting,
 * archiving and deleting are deliberately not commands: those stay behind a visible click.
 */

/** Longest single recording, in seconds. The browser stops it by itself at this point. */
export const VOICE_MAX_SECONDS = 90;

/** The word every command starts with. Callers may pass another word to `parseVoiceInput`. */
export const DEFAULT_COMMAND_WORD = "command";

export const VOICE_COMMAND_IDS = ["save_draft", "submit_for_review", "new_post", "go_content", "go_calendar", "go_review", "go_ads", "undo"] as const;
export type VoiceCommandId = (typeof VOICE_COMMAND_IDS)[number];

export interface VoiceCommand {
  id: VoiceCommandId;
  /** Shown in the microphone's confirmation. */
  label: string;
  /** Everything that may be said after the command word. */
  phrases: readonly string[];
}

export const VOICE_COMMANDS: readonly VoiceCommand[] = [
  { id: "save_draft", label: "Save draft", phrases: ["save draft", "save the draft", "save"] },
  { id: "submit_for_review", label: "Submit for review", phrases: ["submit for review", "send for review", "submit"] },
  { id: "new_post", label: "New post", phrases: ["new post", "new article", "new content"] },
  { id: "go_content", label: "Go to Content", phrases: ["go to content", "open content", "content"] },
  { id: "go_calendar", label: "Go to Calendar", phrases: ["go to calendar", "open calendar", "calendar"] },
  { id: "go_review", label: "Go to Review", phrases: ["go to review", "open review", "review"] },
  { id: "go_ads", label: "Go to Ads", phrases: ["go to ads", "open ads", "ads"] },
  { id: "undo", label: "Undo", phrases: ["undo", "undo that"] },
];

export type VoiceInput =
  | { kind: "text"; text: string }
  | { kind: "command"; id: VoiceCommandId; label: string }
  | { kind: "unknown_command"; heard: string };

/** Lower case, punctuation removed, single spaces: "Command, save draft." → "command save draft". */
export function normalizeSpeech(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseVoiceInput(transcript: string, commandWord: string = DEFAULT_COMMAND_WORD): VoiceInput {
  const text = transcript.trim();
  const word = normalizeSpeech(commandWord);
  const spoken = normalizeSpeech(text);
  if (word === "" || (spoken !== word && !spoken.startsWith(`${word} `))) return { kind: "text", text };

  const rest = spoken.slice(word.length).trim().replace(/ please$/, "");
  const hit = VOICE_COMMANDS.find((c) => c.phrases.includes(rest));
  return hit ? { kind: "command", id: hit.id, label: hit.label } : { kind: "unknown_command", heard: rest };
}

/* Dropping a dictated sentence into existing text. */
const OPENERS = new Set(["(", "[", "{", '"', "“", "‘", "'", "/"]);
const CLOSERS = /^[.,;:!?)\]}%]/;

/** The space (or nothing) to put before `text` so it joins the text before the cursor naturally. */
export function dictationPrefix(charBefore: string | undefined, text: string): string {
  if (!charBefore || /\s/.test(charBefore) || OPENERS.has(charBefore)) return "";
  return CLOSERS.test(text) ? "" : " ";
}

/** Cuts `text` to the room left in a field with a character limit; returns what fits and whether anything was cut. */
export function fitToLimit(text: string, room: number): { text: string; cut: boolean } {
  if (!Number.isFinite(room) || room >= text.length) return { text, cut: false };
  return { text: text.slice(0, Math.max(0, room)), cut: true };
}
